import classnames from 'classnames'
import { CSSProperties, useMemo } from 'react'

// Hooks
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import mergeClasses from '@src/quince-site-common/src/utils/mergeClasses'

const pxToRem = (px?: number) => (px !== undefined ? `${px / 10}rem` : '0rem')

const isDynamicValueClass = (className: string) => {
  const [, value] = className.split('--')
  return !isNaN(parseFloat(value))
}

const makeStyleObject = (
  dynamicValueClasses: string[],
  postFix: string,
  defaultStyle?: Record<string, number>,
  prefix?: string,
): Record<string, string> => {
  const styles = (dynamicValueClasses || []).reduce<Record<string, string>>((style, className) => {
    const [blockWithPerimeter, value] = className.split('--')
    const key = prefix ? `--${prefix}-${blockWithPerimeter}-${postFix}` : `--${blockWithPerimeter}-${postFix}`
    style[key] = pxToRem(parseFloat(value))
    return style
  }, {})

  if (defaultStyle)
    Object.keys(defaultStyle).forEach(style => {
      const key = prefix ? `--${prefix}-${style}-${postFix}` : `--${style}-${postFix}`
      styles[key] = styles[key] ?? `${defaultStyle[style]}rem`
    })

  return styles
}

type UseDynamicStyle = (payload: {
  baseDynamicValueClasses?: string[] | undefined
  dynamicValueClassesMobileOverride?: string[] | undefined
  mobileClassNamesOverride?: string[] | undefined
  baseClassNames?: string[] | undefined
  postFix: string
  wrapperClassName?: string | undefined
  defaultBaseStyle?: Record<string, number> | undefined
  defaultMobileOverridesStyle?: Record<string, number> | undefined
}) => { styleObject: CSSProperties; className: string }

const useDynamicStyle: UseDynamicStyle = ({
  baseDynamicValueClasses,
  dynamicValueClassesMobileOverride,
  mobileClassNamesOverride,
  baseClassNames,
  postFix,
  wrapperClassName,
  defaultBaseStyle,
  defaultMobileOverridesStyle,
}) => {
  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE

  const styleObject = useMemo(() => {
    const baseDynamicValueDimension = makeStyleObject(baseDynamicValueClasses ?? [], postFix, defaultBaseStyle)
    /* eslint-disable i18next/no-literal-string */
    const dynamicValueDimensionMobile = makeStyleObject(
      dynamicValueClassesMobileOverride ?? [],
      postFix,
      defaultMobileOverridesStyle,
      'mobile',
    )
    /* eslint-enable i18next/no-literal-string */

    return {
      ...baseDynamicValueDimension,
      ...dynamicValueDimensionMobile,
    } as CSSProperties
  }, [
    baseDynamicValueClasses,
    dynamicValueClassesMobileOverride,
    postFix,
    defaultBaseStyle,
    defaultMobileOverridesStyle,
  ])

  const nonDynamicClasses = (baseDynamicValueClasses ?? []).filter(c => !isDynamicValueClass(c))

  const className = mergeClasses(
    [...(baseClassNames ?? []), ...nonDynamicClasses],
    isMobile && mobileClassNamesOverride ? mobileClassNamesOverride : [],
  )

  return {
    styleObject,
    className: classnames(wrapperClassName, className),
  }
}

export default useDynamicStyle
